import React from "react";
import { useOutletContext } from "react-router-dom";
import TfGenQuery from "../../../../model/TfGenQuery";
import TFCategorySelector from "./selector/TFCategorySelector";
import { TfGeneratorPageContext } from "./TfGeneratorPage";
import {GeneratorCategory, TfGeneratorModifiers} from "../../../../model/generator/GeneratorTypes";
import ContentBox from "../../../../components/content-box/ContentBox";
import PageActions from "../../../base/PageActions";
import _image_service from "../../../../services/ImageService";
import '../../../styles/Common.scss';
import '../../../styles/Form.scss';
import './TfGeneratorPage.scss';

const TfGeneratorOptionsPage = () => {

    const context = useOutletContext<TfGeneratorPageContext>();
    if (!context) {
        return null; // Parent outlet not mounted yet or route mismatch
    }
    const { query, setQuery, getQuickResult, getGeneratorResult, resetQuery } = context;

    const page: PageActions = new PageActions({
        title: `Random Transformation Generator - Chase the Fox`,
        description: `Feel like changing for a change? What will Chase zap you into next?`,
        keywords: ["TF Generator", "transformation generator", "transformation randomizer", "TF randomizer"],
        imgSrc: `chase/assets/tf_victim.png`,
        url: `tf-generator`
    });

    const isValidForm = (): boolean => {
        return [query.extinct, query.mythical, query.casarokanUnique]
            .filter(v => v === 'ONLY').length < 2;
    }

    const onChange = (value: object): void => {
        setQuery({...query, ...value});
    }

    const isChecked = (queryType: string, queryValue: string): boolean => {
        return query[queryType as keyof TfGenQuery] === queryValue;
    }

    const isDisabled = (value: string): boolean => {
        const otherCategories = ["extinct", "mythical", "casarokanUnique"].filter(v => v !== value);
        return otherCategories.reduce((acc, curr) => query[curr as keyof TfGenQuery] === 'ONLY'? true : acc || false, false);
    }

    const optionsPage = (): JSX.Element => {
        return (
            <div className="tf-generator-layout">
                <div className="tf-generator-controls">
                    <p>
                        I'm a mischevious fox with a magical amulet! I'm sure if you wanted to
                        give another form a try, I could help you out!
                    </p>
                    <form className="form-body tf-generator-form">
                        <div className="tf-form-row">
                            <div>
                                <label htmlFor="category">Category:</label>
                            </div>
                            <div>
                                <select id="category-select"
                                        className="form-select"
                                        name="category"
                                        value={query.category}
                                        onChange={(event) => setQuery({...query, category: event.target.value})}
                                >
                                    {GeneratorCategory.map(key =>
                                        <option key={`tf-gen-category-${key.toLowerCase()}`}
                                                value={key.substring(0, key.endsWith('s') ? key.length - 1 : key.length).toUpperCase()}>
                                            {key}
                                        </option>
                                    )}
                                </select>
                            </div>
                        </div>
                        <TFCategorySelector label="extinct" title="Extinct Creatures" onChange={onChange} isChecked={isChecked} isDisabled={isDisabled} />
                        <TFCategorySelector label="mythical" title="Mythical Creatures" onChange={onChange} isChecked={isChecked} isDisabled={isDisabled} />
                        <TFCategorySelector label="casarokanUnique" title="Casarokan Unique" onChange={onChange} isChecked={isChecked} isDisabled={isDisabled} />
                        <div className="tf-form-row">
                            <div>
                                <label htmlFor="modifier">Modifier:</label>
                            </div>
                            <div>
                                <div className="form-toggle-group">
                                    {Object.keys(TfGeneratorModifiers).map(key =>
                                        <React.Fragment key={`modifier-radio-${key}`}>
                                            <input id={`modifier-${key}`}
                                                   name="modifier"
                                                   type="radio"
                                                   value={key}
                                                   checked={query.modifier === key}
                                                   onChange={() => setQuery({...query, modifier: key})}
                                            />
                                            <label htmlFor={`modifier-${key}`}>{TfGeneratorModifiers[key]}</label>
                                        </React.Fragment>
                                    )}
                                </div>
                            </div>
                        </div>
                        <span className="form-warning tf-form-fullwidth" hidden={isValidForm()}>
                            At most one field can be marked as only!
                        </span>
                    </form>
                    <div className="tf-generator-buttons">
                        <input className="form-button-primary"
                               type="button"
                               value="Transform!"
                               disabled={!isValidForm()}
                               onClick={() => getGeneratorResult(query)}
                               />
                        <input className="form-button-secondary"
                               type="button"
                               value="Reset"
                               onClick={resetQuery}/>
                        <input className="form-button-secondary"
                               type="button"
                               value="Randomize Me!"
                               onClick={() => getQuickResult()}
                               />
                    </div>
                </div>
                <div className="tf-generator-image">
                    <img src={_image_service.getCdnImageSrc('chase/assets/chase_thinking.png')}
                         alt="Hmmm..."
                         title="I'm thinking about what I should do..."/>
                </div>
            </div>
        );
    }

    return page.generateMetaContent(
        <>  
            <ContentBox header="TF Generator">
                {optionsPage()}
            </ContentBox>
        </>
    );
}

export default TfGeneratorOptionsPage;
